import React from 'react'
import type { NextPage } from 'next'
import Image from 'next/image'
import { useEffect, useState, useRef } from 'react'
import Contact from '../../components/contact/Contact'
import Grid from '../../components/grid/Grid'
import CustomImage from '../../components/customImage/CustomImage'
import FadeIn from '../../components/fadeIn/FadeIn'
import styles from '../../styles/pages/Sustainability.module.scss'
import { useIsMobile } from '../../hooks/useIsDesktop'
import CustomMetaData from '../../components/general/customMetaData/CustomMetaData'

import { gsap } from 'gsap'
import { GenericPageLayoutProps } from '../../components/general/genericPageLayout/GenericPageLayout'

import { useGlobalContentContext } from '../../context/GlobalContentContext'

import { parseGenericPage } from '../../utils/parsers'
import { getAPI, createMarkup } from '../../utils/helpers'

type Props = {} & GenericPageLayoutProps

const Industries = (props: Props) => {
  const { setData } = useGlobalContentContext()

  const isMobile = useIsMobile()

  const featuredTextRef = useRef<HTMLDivElement>(null)

  const [featureText, setFeatureText] = useState('')

  useEffect(() => {
    setData(props.globalData)
  }, [props.globalData, setData])

  const handleMouseEnter = (text: string) => {
    if (featuredTextRef.current) {
      gsap.set(featuredTextRef.current, { opacity: 0 })
      gsap.killTweensOf(featuredTextRef.current)
      setFeatureText(text)

      gsap.to(featuredTextRef.current, {
        opacity: 1,
        duration: 0.7,
        ease: 'powerIn',
      })
    }
  }

  const handleMouseLeave = (text: string) => {
    if (featuredTextRef.current) {
      gsap.to(featuredTextRef.current, {
        opacity: 0,
        duration: 0.7,
        delay: 0.25,
        ease: 'powerIn',
        onComplete: () => {
          setFeatureText(text)
        },
      })
    }
  }

  return (
    <div className={styles.container}>
      <CustomMetaData
        title={props.originalResponse.meta_title}
        description={props.originalResponse.meta_description}
        image={props.originalResponse.meta_image}
      />

      <div className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroContentContainer}>
            <h1 className={styles.heroTitle}>{props.originalResponse.title}</h1>
          </div>
          <div className={styles.heroImage}>
            <CustomImage
              src={props.originalResponse.hero}
              layout="fill"
              objectFit="cover"
            />
          </div>
        </section>

        <section className={styles.aboutSection}>
          <div className={styles.maxWidthContainer}>
            <h2 className={styles.aboutSectionTitle}>
              {props.originalResponse.aboutsection.title}
            </h2>
            <div
              className={styles.aboutSectionDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.aboutsection.detail
              )}
            ></div>

            <h2 className={styles.aboutSectionTitle}>
              {props.originalResponse.aboutsection.secondary_title}
            </h2>
            <div
              className={styles.aboutSectionDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.aboutsection.secondary_detail
              )}
            ></div>
          </div>
        </section>

        <section className={styles.aboutSectionBlocks}>
          <h2 className={styles.aboutSectionBlocksTitle}>
            {props.originalResponse.aboutsection.about_blocks_title}
          </h2>
          <div
            className={styles.aboutSectionBlocksDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.aboutsection.about_blocks_detail
            )}
          ></div>

          <div className={styles.aboutSectionBlocksContainer}>
            <div className={styles.aboutSectionBlocksContent}>
              {props.originalResponse.about_blocks.map(
                (block: any, index: number) => (
                  <div key={index} className={styles.aboutSectionBlock}>
                    <h3 className={styles.aboutSectionBlockTitle}>
                      {block.title}
                    </h3>
                    <div
                      className={styles.aboutSectionBlockDetail}
                      dangerouslySetInnerHTML={createMarkup(block.detail)}
                    ></div>
                  </div>
                )
              )}
            </div>
            <div className={styles.aboutSectionBlockImage}>
              <CustomImage
                src={props.originalResponse.aboutsection}
                layout="responsive"
              />
            </div>
          </div>
        </section>

        <section className={styles.detailSection}>
          <h2 className={styles.detailSectionTitle}>
            {props.originalResponse.detailssection.title}
          </h2>

          <div className={styles.detailSectionGraphic}>
            {props.originalResponse.details_graphic_blocks.map(
              (block: any, index: number) => (
                <React.Fragment key={index}>
                  <div
                    key={index}
                    className={`${styles.graphicBlock} ${
                      isMobile && featureText === block.detail
                        ? styles.graphicBlockHovered
                        : ''
                    }`}
                    onMouseEnter={() => handleMouseEnter(block.detail)}
                    onMouseLeave={() => handleMouseLeave('')}
                    onTouchStart={() =>
                      featureText === block.detail
                        ? setFeatureText('')
                        : setFeatureText(block.detail)
                    }
                  >
                    <div className={styles.graphicBlockImage}>
                      <CustomImage src={block} layout="responsive" />
                    </div>
                    <div className={styles.graphicBlockTitle}>
                      {block.title}
                    </div>
                  </div>
                  <div
                    className={`${styles.graphicArrow} ${
                      isMobile && featureText === block.detail
                        ? styles.graphicArrowHovered
                        : ''
                    }`}
                  >
                    <Image
                      src="/graphic-arrow.svg"
                      layout="fill"
                      alt=""
                    ></Image>
                  </div>
                </React.Fragment>
              )
            )}

            <div
              ref={featuredTextRef}
              className={`${
                featureText.length > 0
                  ? styles.graphicFeaturedTextActive
                  : styles.graphicFeaturedText
              }`}
              dangerouslySetInnerHTML={createMarkup(featureText)}
            ></div>
          </div>

          <div className={styles.detailSectionBlocks}>
            {props.originalResponse.details_section_blocks.map(
              (block: any, index: number) => (
                <FadeIn key={index}>
                  <div className={styles.detailBlock}>
                    <div className={styles.detailBlockImage}>
                      <CustomImage src={block} layout="responsive" />
                    </div>
                    <h3 className={styles.detailBlockTitle}>{block.title}</h3>
                    <div
                      className={styles.detailBlockDetail}
                      dangerouslySetInnerHTML={createMarkup(block.detail)}
                    ></div>
                    {(block.property_detail_1 ||
                      block.property_detail_2 ||
                      block.property_detail_3) && (
                      <Grid columns={3}>
                        {block.property_detail_1 && (
                          <div
                            className={styles.detailBlockProperty}
                            dangerouslySetInnerHTML={createMarkup(
                              block.property_detail_1
                            )}
                          ></div>
                        )}
                        {block.property_detail_2 && (
                          <div
                            className={styles.detailBlockProperty}
                            dangerouslySetInnerHTML={createMarkup(
                              block.property_detail_2
                            )}
                          ></div>
                        )}
                        {block.property_detail_3 && (
                          <div
                            className={styles.detailBlockProperty}
                            dangerouslySetInnerHTML={createMarkup(
                              block.property_detail_3
                            )}
                          ></div>
                        )}
                      </Grid>
                    )}
                  </div>
                </FadeIn>
              )
            )}
          </div>
        </section>

        <Contact />
      </div>
    </div>
  )
}

export async function getStaticProps() {
  const page: GenericPageLayoutProps = await parseGenericPage(
    `${getAPI()}/environmentpage/`
  )
  return {
    props: {
      originalResponse: page.originalResponse,
      title: page.title,
      globalData: page.globalData,
      meta_title: page.meta_title,
      meta_description: page.meta_description,
    },
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  }
}

export const NEXTJS_REVALIDATE_PAGE_IN_SECONDS = 10
export default Industries
